/**
 * Platform Admin → Clients
 * Multi-tenant client list + detail tabs.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Building2,
  RefreshCw,
  Plus,
  Users,
  Laptop,
  Shield,
  Palette,
  Settings,
  FileText,
  ArrowLeft,
  Check,
  X,
  MessageSquare,
  Send,
  Trash2,
} from 'lucide-react';
import { apiService } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { PLATFORM_MODULE_CATALOG } from '../config/navigation';
import BulkEnrollmentPanel from './BulkEnrollmentPanel';
import { HubTabs } from './ui/HubShell';

function EntitlementsEditor({
  tenantId,
  mode,
}: {
  tenantId: string;
  mode: 'os' | 'pages' | 'actions';
}) {
  const toast = useToast();
  const [data, setData] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    try {
      const ent = await apiService.platformGetEntitlements(tenantId);
      setData(ent);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load entitlements');
    }
  }, [tenantId, toast]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!data) return <p className="text-sm text-slate-500">Loading entitlements…</p>;

  if (mode === 'os') {
    return (
      <div className="space-y-3 text-sm max-w-xl">
        <p className="text-slate-600">Enable OS platforms and enrollment modes for this client.</p>
        {(data.os || []).map((row: any, idx: number) => (
          <div key={row.platform} className="border rounded-lg p-3 space-y-2">
            <label className="flex items-center gap-2 font-medium">
              <input
                type="checkbox"
                checked={!!row.enabled}
                onChange={(e) => {
                  const next = [...data.os];
                  next[idx] = { ...row, enabled: e.target.checked };
                  setData({ ...data, os: next });
                }}
              />
              {row.platform}
            </label>
            <input
              className="w-full border rounded px-2 py-1 text-xs font-mono"
              value={(row.allowed_modes || []).join(', ')}
              onChange={(e) => {
                const modes = e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean);
                const next = [...data.os];
                next[idx] = { ...row, allowed_modes: modes };
                setData({ ...data, os: next });
              }}
              placeholder="AGENT_MANAGED, WORK_PROFILE, …"
            />
          </div>
        ))}
        <button
          type="button"
          disabled={saving}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm"
          onClick={async () => {
            setSaving(true);
            try {
              await apiService.platformPutOsEntitlements(tenantId, data.os || []);
              await reload();
              toast.success('OS access saved');
            } catch (e: any) {
              toast.error(e?.message || 'Save failed');
            } finally {
              setSaving(false);
            }
          }}
        >
          Save OS access
        </button>
      </div>
    );
  }

  if (mode === 'pages') {
    return (
      <div className="space-y-2 text-sm max-w-xl">
        <p className="text-slate-600">Pages this client may access in the Hub.</p>
        {(data.pages || []).map((row: any, idx: number) => (
          <label key={row.page_key} className="flex items-center justify-between py-2 border-b border-slate-100">
            <span>{row.label || row.page_key}</span>
            <input
              type="checkbox"
              checked={!!row.enabled}
              onChange={(e) => {
                const next = [...data.pages];
                next[idx] = { ...row, enabled: e.target.checked };
                setData({ ...data, pages: next });
              }}
            />
          </label>
        ))}
        <button
          type="button"
          disabled={saving}
          className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm"
          onClick={async () => {
            setSaving(true);
            try {
              await apiService.platformPutPageEntitlements(
                tenantId,
                (data.pages || []).map((p: any) => ({ page_key: p.page_key, enabled: !!p.enabled }))
              );
              await reload();
              toast.success('Page access saved');
            } catch (e: any) {
              toast.error(e?.message || 'Save failed');
            } finally {
              setSaving(false);
            }
          }}
        >
          Save page access
        </button>
      </div>
    );
  }

  // actions
  const dangerous = (data.actions || []).filter((a: any) => a.is_dangerous);
  const normal = (data.actions || []).filter((a: any) => !a.is_dangerous);
  const toggle = (key: string, enabled: boolean) => {
    setData({
      ...data,
      actions: (data.actions || []).map((a: any) =>
        a.permission_key === key ? { ...a, enabled } : a
      ),
    });
  };
  return (
    <div className="space-y-4 text-sm">
      <p className="text-slate-600">
        Action permissions Platform Admin allows for this client. Client Admin can only assign subsets of these.
      </p>
      <div>
        <p className="font-semibold text-amber-700 mb-2">Dangerous actions</p>
        <div className="max-h-48 overflow-auto space-y-1 border border-amber-200 rounded-lg p-2 bg-amber-50">
          {dangerous.map((a: any) => (
            <label key={a.permission_key} className="flex items-start gap-2 text-xs">
              <input type="checkbox" checked={!!a.enabled} onChange={(e) => toggle(a.permission_key, e.target.checked)} />
              <span>
                <span className="font-mono">{a.permission_key}</span>
                <span className="ml-1 px-1 rounded bg-red-100 text-red-700">dangerous</span>
                <div className="text-slate-500">{a.description}</div>
              </span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <p className="font-semibold mb-2">Standard actions</p>
        <div className="max-h-64 overflow-auto space-y-1 border rounded-lg p-2">
          {normal.map((a: any) => (
            <label key={a.permission_key} className="flex items-start gap-2 text-xs">
              <input type="checkbox" checked={!!a.enabled} onChange={(e) => toggle(a.permission_key, e.target.checked)} />
              <span>
                <span className="font-mono">{a.permission_key}</span>
                <div className="text-slate-500">{a.description}</div>
              </span>
            </label>
          ))}
        </div>
      </div>
      <button
        type="button"
        disabled={saving}
        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm"
        onClick={async () => {
          setSaving(true);
          try {
            await apiService.platformPutActionEntitlements(
              tenantId,
              (data.actions || []).map((a: any) => ({
                permission_key: a.permission_key,
                enabled: !!a.enabled,
              }))
            );
            await reload();
            toast.success('Action permissions saved');
          } catch (e: any) {
            toast.error(e?.message || 'Save failed');
          } finally {
            setSaving(false);
          }
        }}
      >
        Save action permissions
      </button>
    </div>
  );
}

function CompliancePackEditor({
  tenantId,
  deviceCount,
}: {
  tenantId: string;
  deviceCount: number;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState({ rules_json: '[]', thresholds_json: '{}', notes: '' });
  const [ruleCount, setRuleCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pack = await apiService.platformGetCompliancePack(tenantId);
        if (cancelled) return;
        setDraft({
          rules_json: JSON.stringify(pack?.rules || [], null, 2),
          thresholds_json: JSON.stringify(pack?.thresholds || {}, null, 2),
          notes: pack?.notes || '',
        });
        setRuleCount(Array.isArray(pack?.rules) ? pack.rules.length : 0);
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load compliance pack');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, toast]);

  return (
    <div className="text-sm space-y-3 max-w-2xl">
      <p className="text-slate-600">
        Tenant-scoped compliance pack ({deviceCount} devices). Global engine remains at Hub → Compliance.
      </p>
      <label className="block text-xs">
        Rules (JSON array)
        <textarea
          className="mt-1 w-full border rounded-lg px-3 py-2 font-mono text-xs"
          rows={6}
          value={draft.rules_json}
          onChange={(e) => setDraft({ ...draft, rules_json: e.target.value })}
        />
      </label>
      <label className="block text-xs">
        Thresholds (JSON)
        <textarea
          className="mt-1 w-full border rounded-lg px-3 py-2 font-mono text-xs"
          rows={4}
          value={draft.thresholds_json}
          onChange={(e) => setDraft({ ...draft, thresholds_json: e.target.value })}
        />
      </label>
      <label className="block text-xs">
        Notes
        <input
          className="mt-1 w-full border rounded-lg px-3 py-2"
          value={draft.notes}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
        />
      </label>
      <button
        type="button"
        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm"
        onClick={async () => {
          try {
            const rules = JSON.parse(draft.rules_json || '[]');
            const thresholds = JSON.parse(draft.thresholds_json || '{}');
            await apiService.platformPutCompliancePack(tenantId, {
              rules,
              thresholds,
              notes: draft.notes || null,
            });
            setRuleCount(Array.isArray(rules) ? rules.length : 0);
            toast.success('Compliance pack saved');
          } catch (e: any) {
            toast.error(e?.message || 'Failed to save compliance pack');
          }
        }}
      >
        Save compliance pack
      </button>
      <p className="text-xs text-slate-400">{ruleCount} rule(s) · API: /api/v1/platform/tenants/&#123;id&#125;/compliance-pack</p>
    </div>
  );
}

type TenantSummary = {
  id: string;
  name: string;
  code: string;
  status: string;
  enabled_modules?: string[];
  user_count?: number;
  device_count?: number;
  created_at?: string | null;
  company_profile?: Record<string, unknown>;
  license_limits?: Record<string, unknown>;
  modules?: Record<string, boolean>;
  branding?: Record<string, string | null>;
  security?: Record<string, unknown>;
  enrollment_settings?: Record<string, unknown>;
  group_count?: number;
  invite_count?: number;
  package?: {
    source_mode?: string;
    uem_relative_path?: string;
    uem_absolute_path?: string;
    quota_bytes?: number;
    used_bytes?: number;
    quota_gb?: number;
    used_gb?: number;
    allow_client_share?: boolean;
    client_share_base?: string;
  };
};

const DETAIL_TABS = [
  'Overview',
  'Company Profile',
  'Modules',
  'OS Access',
  'Page Access',
  'Action Permissions',
  'Users',
  'Groups',
  'Enrollment Settings',
  'Enrollment Invites',
  'Policies & Defaults',
  'Branding',
  'Security / SSO',
  'Compliance Rules',
  'License / Limits',
  'Package Storage',
  'Buntu',
  'Audit Logs',
] as const;

type DetailTab = (typeof DETAIL_TABS)[number];

/** Build tenant code from company name words (3–4 letters) + year, e.g. "Varna" → "VARN2026", "Acme Corp" → "ACM2026". */
export function suggestTenantCode(name: string, year = new Date().getFullYear()): string {
  const words = name
    .trim()
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter(Boolean);
  if (!words.length) return `ORG${year}`;
  let letters = '';
  if (words.length === 1) {
    letters = words[0].slice(0, 4);
  } else if (words.length === 2) {
    // Prefer 3 letters from the first word when long enough (Acme Corp → ACM),
    // otherwise combine both words (IT Corp → ITC).
    letters = words[0].length >= 3 ? words[0].slice(0, 3) : (words[0] + words[1]).slice(0, 3);
  } else {
    letters = words.map((w) => w[0]).join('').slice(0, 4);
  }
  if (letters.length < 3) letters = (letters + 'XXX').slice(0, 3);
  return `${letters}${year}`;
}

const EMPTY_CREATE = {
  name: '',
  code: '',
  package_quota_gb: '5',
  package_path: '',
  allow_client_share: true,
  plan_name: 'Standard',
  plan_code: 'STD',
  billing_cycle: 'monthly',
  trial_days: '14',
  primary_domain: '',
  client_admin_name: '',
  client_admin_email: '',
  client_admin_phone: '',
  client_admin_password: 'Welcome@123',
  theme_color: '#4f46e5',
  support_email: '',
  support_phone: '',
  timezone: 'UTC',
  billing_email: '',
  plan_amount: '',
  max_devices: '1000',
  max_users: '100',
  enabled_os: 'windows,linux,macos,android,ios',
  buntu_enabled: true,
  buntu_required_for_admins: false,
};

export default function PlatformAdminClients({
  onEnterPortal,
  onNavigate,
}: {
  onEnterPortal?: (tenantId: string, tenantName: string) => void;
  onNavigate?: (tab: string) => void;
} = {}) {
  const toast = useToast();
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TenantSummary | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('Overview');
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ ...EMPTY_CREATE });
  const [codeTouched, setCodeTouched] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showBuntuBroadcast, setShowBuntuBroadcast] = useState(false);
  const [buntuBroadcast, setBuntuBroadcast] = useState({
    body: '',
    priority: 'important',
    delivery: 'until_dismissed',
  });
  const [buntuSending, setBuntuSending] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [audit, setAudit] = useState<any[]>([]);
  const [brandDraft, setBrandDraft] = useState({ theme_color: '#4f46e5', login_message: '', enrollment_message: '', support_contact: '' });
  const [profileDraft, setProfileDraft] = useState({ name: '', status: 'active' });
  const [modulesDraft, setModulesDraft] = useState<Record<string, boolean>>({});
  const [newUser, setNewUser] = useState({ username: '', password: '', full_name: '', email: '', role: 'CLIENT_ADMIN' });
  const [editingTenantUserId, setEditingTenantUserId] = useState<string | null>(null);
  const [newGroup, setNewGroup] = useState({ name: '', group_type: 'department' });
  const [enrollDraft, setEnrollDraft] = useState({
    enabled_os: 'windows,linux,macos,android,ios',
    allowed_modes_json: '{}',
    default_policies_json: '{}',
  });
  const [securityDraft, setSecurityDraft] = useState({ sso_enabled: false, sso_config_json: '{}', password_policy_json: '{}' });
  const [licenseDraft, setLicenseDraft] = useState({ max_devices: '10000', max_users: '500' });
  const [packageDraft, setPackageDraft] = useState({
    uem_relative_path: '',
    quota_gb: '5',
    allow_client_share: true,
  });
  const [buntuDraft, setBuntuDraft] = useState({
    enabled: true,
    required_for_admins: false,
  });
  const [buntuLoading, setBuntuLoading] = useState(false);
  const [buntuMsg, setBuntuMsg] = useState({
    body: '',
    priority: 'important',
    delivery: 'until_dismissed',
    sticky_minutes: 5,
    audience: 'client_admins',
  });
  const [buntuMsgSending, setBuntuMsgSending] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmCode, setDeleteConfirmCode] = useState('');
  const [deleting, setDeleting] = useState(false);

  const loadTenants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await apiService.platformListTenants();
      setTenants(Array.isArray(list) ? list : []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const openDetail = async (id: string) => {
    setSelectedId(id);
    setDetailTab('Overview');
    try {
      const d = await apiService.platformGetTenant(id);
      setDetail(d);
      setProfileDraft({ name: d.name, status: d.status });
      setModulesDraft(() => {
        const fromApi = d.modules || {};
        const merged: Record<string, boolean> = {};
        for (const { key } of PLATFORM_MODULE_CATALOG) {
          merged[key] = !!fromApi[key];
        }
        // Keep any custom/legacy keys returned by API
        for (const [key, enabled] of Object.entries(fromApi)) {
          if (!(key in merged)) merged[key] = !!enabled;
        }
        return merged;
      });
      setBrandDraft({
        theme_color: d.branding?.theme_color || '#4f46e5',
        login_message: d.branding?.login_message || '',
        enrollment_message: d.branding?.enrollment_message || '',
        support_contact: d.branding?.support_contact || '',
      });
      const es = d.enrollment_settings || {};
      setEnrollDraft({
        enabled_os: Array.isArray(es.enabled_os) ? es.enabled_os.join(',') : 'windows,linux,macos,android,ios',
        allowed_modes_json: JSON.stringify(es.allowed_modes || {}, null, 2),
        default_policies_json: JSON.stringify(es.default_policies || {}, null, 2),
      });
      const sec = d.security || {};
      setSecurityDraft({
        sso_enabled: !!sec.sso_enabled,
        sso_config_json: JSON.stringify(sec.sso_config || {}, null, 2),
        password_policy_json: JSON.stringify(sec.password_policy || {}, null, 2),
      });
      const lim = d.license_limits || {};
      setLicenseDraft({
        max_devices: String(lim.max_devices ?? 10000),
        max_users: String(lim.max_users ?? 500),
      });
      try {
        const pkg = d.package || (await apiService.platformGetPackageSettings(id));
        setPackageDraft({
          uem_relative_path: pkg?.uem_relative_path || d.code || '',
          quota_gb: String(pkg?.quota_gb ?? 5),
          allow_client_share: pkg?.allow_client_share !== false,
        });
      } catch {
        setPackageDraft({
          uem_relative_path: d.code || '',
          quota_gb: '5',
          allow_client_share: true,
        });
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load client');
    }
  };

  useEffect(() => {
    try {
      const openId = sessionStorage.getItem('platform_open_tenant_id');
      if (openId) {
        sessionStorage.removeItem('platform_open_tenant_id');
        openDetail(openId);
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    if (detailTab === 'Users') {
      apiService.platformListTenantUsers(selectedId).then(setUsers).catch(() => setUsers([]));
    }
    if (detailTab === 'Groups') {
      apiService.platformListTenantGroups(selectedId).then(setGroups).catch(() => setGroups([]));
    }
    if (detailTab === 'Audit Logs') {
      apiService.platformTenantAudit(selectedId).then(setAudit).catch(() => setAudit([]));
    }
    if (detailTab === 'Buntu') {
      setBuntuLoading(true);
      apiService
        .platformGetTenantBuntuSettings(selectedId)
        .then((data) => {
          const en = data?.config?.enablement || {};
          setBuntuDraft({
            enabled: en.enabled !== false,
            required_for_admins: !!en.required_for_admins,
          });
        })
        .catch(() => {
          setBuntuDraft({ enabled: true, required_for_admins: false });
        })
        .finally(() => setBuntuLoading(false));
    }
  }, [selectedId, detailTab]);

  const handleCreate = async () => {
    const name = createForm.name.trim();
    if (!name) {
      setCreateError('Company name is required');
      return;
    }
    const code = (createForm.code.trim() || suggestTenantCode(name)).toUpperCase();
    setSaving(true);
    setCreateError(null);
    setError(null);
    try {
      const enabledOs = createForm.enabled_os
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      await apiService.platformCreateTenant({
        name,
        code,
        package_quota_gb: Number(createForm.package_quota_gb) || 5,
        package_path: (createForm.package_path.trim() || code).toUpperCase(),
        allow_client_share: createForm.allow_client_share,
        plan_name: createForm.plan_name || 'Standard',
        plan_code: createForm.plan_code || 'STD',
        billing_cycle: createForm.billing_cycle || 'monthly',
        trial_days: Number(createForm.trial_days) || 14,
        primary_domain: createForm.primary_domain.trim() || null,
        client_admin_name: createForm.client_admin_name.trim() || null,
        client_admin_email: createForm.client_admin_email.trim() || null,
        client_admin_phone: createForm.client_admin_phone.trim() || null,
        client_admin_password: createForm.client_admin_password || null,
        theme_color: createForm.theme_color || null,
        support_email: createForm.support_email.trim() || null,
        support_phone: createForm.support_phone.trim() || null,
        timezone: createForm.timezone || null,
        billing_email: createForm.billing_email.trim() || null,
        plan_amount: createForm.plan_amount ? Number(createForm.plan_amount) : null,
        enabled_os: enabledOs.length ? enabledOs : null,
        license_limits: {
          max_devices: Number(createForm.max_devices) || 1000,
          max_users: Number(createForm.max_users) || 100,
        },
        buntu_enabled: createForm.buntu_enabled,
        buntu_required_for_admins: createForm.buntu_required_for_admins,
      });
      setShowCreate(false);
      setCreateForm({ ...EMPTY_CREATE });
      setCodeTouched(false);
      toast.success(`Client ${name} created`, 'Created successfully');
      await loadTenants();
    } catch (e: any) {
      const msg = e?.message || 'Create failed';
      const friendly =
        msg === 'tenant_code_exists'
          ? `Code ${code} is already used. Change the code or clear it to auto-generate.`
          : msg;
      setCreateError(friendly);
      toast.error(friendly, 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  if (selectedId && detail) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => {
            setSelectedId(null);
            setDetail(null);
            loadTenants();
          }}
          className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:underline"
        >
          <ArrowLeft size={14} /> Back to clients
        </button>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <Building2 size={20} /> {detail.name}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Code <span className="font-mono font-semibold">{detail.code}</span> ·{' '}
                <span
                  className={
                    detail.status === 'active' || detail.status === 'trial'
                      ? 'text-emerald-600'
                      : detail.status === 'deleted'
                        ? 'text-red-600'
                        : 'text-amber-600'
                  }
                >
                  {detail.status}
                </span>
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-sm text-slate-600 items-center justify-end">
              <span className="inline-flex items-center gap-1 mr-2"><Users size={14} /> {detail.user_count ?? 0} users</span>
              <span className="inline-flex items-center gap-1 mr-2"><Laptop size={14} /> {detail.device_count ?? 0} devices</span>
              {onEnterPortal && detail.status !== 'deleted' && (
                <button
                  type="button"
                  onClick={() => onEnterPortal(detail.id, detail.name)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium"
                >
                  Enter client portal
                </button>
              )}
              {detail.status === 'active' || detail.status === 'trial' ? (
                <button
                  type="button"
                  className="px-3 py-1.5 border border-amber-300 text-amber-800 rounded-lg text-sm"
                  onClick={async () => {
                    await apiService.platformSuspendTenant(detail.id);
                    toast.success('Client suspended');
                    openDetail(detail.id);
                  }}
                >
                  Suspend
                </button>
              ) : (
                <button
                  type="button"
                  className="px-3 py-1.5 border border-emerald-300 text-emerald-800 rounded-lg text-sm"
                  onClick={async () => {
                    await apiService.platformActivateTenant(detail.id);
                    toast.success(detail.status === 'deleted' ? 'Client restored' : 'Client activated');
                    openDetail(detail.id);
                  }}
                >
                  {detail.status === 'deleted' ? 'Restore' : 'Activate'}
                </button>
              )}
              {detail.status !== 'deleted' && String(detail.code || '').toUpperCase() !== 'NUTRINIUM' && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-300 text-red-700 rounded-lg text-sm"
                  onClick={() => {
                    setDeleteConfirmCode('');
                    setShowDeleteConfirm(true);
                  }}
                >
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </div>
          </div>
        </div>

        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl border border-slate-200 shadow-xl max-w-md w-full p-5 space-y-3">
              <h3 className="text-lg font-semibold text-slate-900">Delete client</h3>
              <p className="text-sm text-slate-600">
                This soft-deletes <span className="font-semibold">{detail.name}</span> (
                <span className="font-mono">{detail.code}</span>). Client logins and enrollment are blocked.
                Data is retained; you can Restore later. Type the tenant code to confirm.
              </p>
              <input
                type="text"
                autoFocus
                value={deleteConfirmCode}
                onChange={(e) => setDeleteConfirmCode(e.target.value)}
                placeholder={detail.code}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
              />
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  disabled={deleting}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={
                    deleting ||
                    deleteConfirmCode.trim().toUpperCase() !== String(detail.code || '').toUpperCase()
                  }
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm disabled:opacity-50"
                  onClick={async () => {
                    setDeleting(true);
                    try {
                      await apiService.platformDeleteTenant(detail.id, deleteConfirmCode.trim());
                      toast.success('Client deleted');
                      setShowDeleteConfirm(false);
                      setSelectedId(null);
                      setDetail(null);
                      await loadTenants();
                    } catch (e: any) {
                      toast.error(e?.message || 'Failed to delete client');
                    } finally {
                      setDeleting(false);
                    }
                  }}
                >
                  <Trash2 size={14} /> {deleting ? 'Deleting…' : 'Delete client'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="hub-panel hub-panel--with-tabs">
          <HubTabs
            variant="segment"
            value={detailTab}
            onChange={setDetailTab}
            items={DETAIL_TABS.map((tab) => ({ id: tab, label: tab }))}
            className="!mx-3 !mt-3"
          />
          <div className="hub-panel__inner min-h-[280px]">
          {detailTab === 'Overview' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="p-3 rounded-lg bg-slate-50">
                <p className="text-slate-500">Devices</p>
                <p className="text-2xl font-bold">{detail.device_count ?? 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-50">
                <p className="text-slate-500">Users</p>
                <p className="text-2xl font-bold">{detail.user_count ?? 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-50">
                <p className="text-slate-500">Groups / Invites</p>
                <p className="text-2xl font-bold">{detail.group_count ?? 0} / {detail.invite_count ?? 0}</p>
              </div>
              <div className="md:col-span-3">
                <p className="text-slate-500 mb-1">Enabled modules</p>
                <div className="flex flex-wrap gap-1.5">
                  {(detail.enabled_modules || []).map((m) => (
                    <span key={m} className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium">
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {detailTab === 'Company Profile' && (
            <div className="max-w-lg space-y-3">
              <label className="block text-sm">
                <span className="text-slate-600">Company name</span>
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={profileDraft.name}
                  onChange={(e) => setProfileDraft({ ...profileDraft, name: e.target.value })}
                />
              </label>
              <label className="block text-sm">
                <span className="text-slate-600">Status</span>
                <select
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={profileDraft.status}
                  onChange={(e) => setProfileDraft({ ...profileDraft, status: e.target.value })}
                >
                  <option value="active">active</option>
                  <option value="suspended">suspended</option>
                  <option value="trial">trial</option>
                </select>
              </label>
              <button
                type="button"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm"
                onClick={async () => {
                  try {
                    await apiService.platformUpdateTenant(selectedId, profileDraft);
                    await openDetail(selectedId);
                    toast.success('Company profile saved');
                  } catch (e: any) {
                    toast.error(e?.message || 'Failed to save profile');
                  }
                }}
              >
                Save profile
              </button>
            </div>
          )}

          {detailTab === 'Modules' && (
            <div className="space-y-2">
              <p className="text-sm text-slate-500 mb-2">
                Platform module access for this client. Tenant users always see these modules in navigation;
                disabled modules open as No Access until enabled here.
              </p>
              {Object.keys(modulesDraft).length === 0 && (
                <p className="text-sm text-slate-500">No modules configured.</p>
              )}
              {Object.entries(modulesDraft).map(([key, enabled]) => {
                const label = PLATFORM_MODULE_CATALOG.find((m) => m.key === key)?.label || key;
                return (
                  <label key={key} className="flex items-center justify-between py-2 border-b border-slate-100 text-sm">
                    <span className="font-medium text-slate-700">
                      {label}
                      <span className="ml-2 text-xs font-normal text-slate-400 font-mono">{key}</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={!!enabled}
                      onChange={(e) => setModulesDraft({ ...modulesDraft, [key]: e.target.checked })}
                    />
                  </label>
                );
              })}
              <button
                type="button"
                className="mt-3 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm inline-flex items-center gap-1"
                onClick={async () => {
                  try {
                    await apiService.platformUpdateModules(selectedId, modulesDraft);
                    await openDetail(selectedId);
                    toast.success('Modules saved');
                  } catch (e: any) {
                    toast.error(e?.message || 'Failed to save modules');
                  }
                }}
              >
                <Shield size={14} /> Save modules
              </button>
            </div>
          )}

          {(detailTab === 'OS Access' || detailTab === 'Page Access' || detailTab === 'Action Permissions') && selectedId && (
            <EntitlementsEditor
              tenantId={selectedId}
              mode={detailTab === 'OS Access' ? 'os' : detailTab === 'Page Access' ? 'pages' : 'actions'}
            />
          )}

          {detailTab === 'Users' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2">
                <input
                  className="border rounded-lg px-2 py-1.5 text-sm"
                  placeholder="Username"
                  value={newUser.username}
                  disabled={!!editingTenantUserId}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                />
                <input
                  className="border rounded-lg px-2 py-1.5 text-sm"
                  placeholder={editingTenantUserId ? 'New password (optional)' : 'Password'}
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                />
                <input
                  className="border rounded-lg px-2 py-1.5 text-sm"
                  placeholder="Email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                />
                <input
                  className="border rounded-lg px-2 py-1.5 text-sm"
                  placeholder="Full name"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                />
                <select className="border rounded-lg px-2 py-1.5 text-sm" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                  <option value="CLIENT_ADMIN">CLIENT_ADMIN</option>
                  <option value="CLIENT_MANAGER">CLIENT_MANAGER</option>
                  <option value="CLIENT_VIEWER">CLIENT_VIEWER</option>
                  <option value="DEVICE_USER">DEVICE_USER</option>
                </select>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="flex-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm"
                    onClick={async () => {
                      try {
                        if (editingTenantUserId) {
                          const body: Record<string, unknown> = {
                            role: newUser.role,
                            email: newUser.email || null,
                            full_name: newUser.full_name || null,
                          };
                          if (newUser.password) body.password = newUser.password;
                          await apiService.platformUpdateTenantUser(selectedId!, editingTenantUserId, body);
                          toast.success('User updated');
                        } else {
                          await apiService.platformCreateTenantUser(selectedId!, newUser);
                          toast.success('User added');
                        }
                        setNewUser({ username: '', password: '', full_name: '', email: '', role: 'CLIENT_ADMIN' });
                        setEditingTenantUserId(null);
                        setUsers(await apiService.platformListTenantUsers(selectedId!));
                      } catch (e: any) {
                        toast.error(e?.message || (editingTenantUserId ? 'Failed to update user' : 'Failed to add user'));
                      }
                    }}
                  >
                    {editingTenantUserId ? 'Save' : 'Add user'}
                  </button>
                  {editingTenantUserId && (
                    <button
                      type="button"
                      className="px-3 py-1.5 border rounded-lg text-sm"
                      onClick={() => {
                        setEditingTenantUserId(null);
                        setNewUser({ username: '', password: '', full_name: '', email: '', role: 'CLIENT_ADMIN' });
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2">Username</th>
                    <th>Role</th>
                    <th>Email</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-50">
                      <td className="py-2 font-medium">{u.username}</td>
                      <td>{u.role}</td>
                      <td>{u.email || '—'}</td>
                      <td>{u.is_active ? <Check size={14} className="text-emerald-600" /> : <X size={14} className="text-rose-500" />}</td>
                      <td className="space-x-2 whitespace-nowrap">
                        <button
                          type="button"
                          className="text-indigo-600"
                          onClick={() => {
                            setEditingTenantUserId(u.id);
                            setNewUser({
                              username: u.username || '',
                              password: '',
                              full_name: u.full_name || '',
                              email: u.email || '',
                              role: u.role || 'CLIENT_ADMIN',
                            });
                          }}
                        >
                          Edit
                        </button>
                        {u.is_active ? (
                          <button
                            type="button"
                            className="text-red-600"
                            onClick={async () => {
                              if (!window.confirm(`Disable user ${u.username}?`)) return;
                              try {
                                await apiService.platformDeleteTenantUser(selectedId!, u.id);
                                setUsers(await apiService.platformListTenantUsers(selectedId!));
                                toast.success('User disabled');
                              } catch (e: any) {
                                toast.error(e?.message || 'Failed to disable user');
                              }
                            }}
                          >
                            Delete
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="text-emerald-600"
                            onClick={async () => {
                              try {
                                await apiService.platformUpdateTenantUser(selectedId!, u.id, { is_active: true });
                                setUsers(await apiService.platformListTenantUsers(selectedId!));
                                toast.success('User enabled');
                              } catch (e: any) {
                                toast.error(e?.message || 'Failed to enable user');
                              }
                            }}
                          >
                            Enable
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {detailTab === 'Groups' && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <input className="border rounded-lg px-2 py-1.5 text-sm flex-1" placeholder="Group name" value={newGroup.name} onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })} />
                <select className="border rounded-lg px-2 py-1.5 text-sm" value={newGroup.group_type} onChange={(e) => setNewGroup({ ...newGroup, group_type: e.target.value })}>
                  <option value="group">group</option>
                  <option value="department">department</option>
                  <option value="location">location</option>
                </select>
                <button
                  type="button"
                  className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm"
                  onClick={async () => {
                    try {
                      await apiService.platformCreateTenantGroup(selectedId, newGroup);
                      setNewGroup({ name: '', group_type: 'department' });
                      setGroups(await apiService.platformListTenantGroups(selectedId));
                      toast.success('Group added');
                    } catch (e: any) {
                      toast.error(e?.message || 'Failed to add group');
                    }
                  }}
                >
                  Add
                </button>
              </div>
              <ul className="divide-y">
                {groups.map((g) => (
                  <li key={g.id} className="py-2 text-sm flex justify-between">
                    <span className="font-medium">{g.name}</span>
                    <span className="text-slate-500">{g.group_type}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {detailTab === 'Branding' && (
            <div className="max-w-lg space-y-3">
              <label className="block text-sm">
                Theme color
                <input type="color" className="mt-1 block" value={brandDraft.theme_color} onChange={(e) => setBrandDraft({ ...brandDraft, theme_color: e.target.value })} />
              </label>
              <label className="block text-sm">
                Login message
                <textarea className="mt-1 w-full border rounded-lg px-3 py-2" rows={2} value={brandDraft.login_message} onChange={(e) => setBrandDraft({ ...brandDraft, login_message: e.target.value })} />
              </label>
              <label className="block text-sm">
                Enrollment message
                <textarea className="mt-1 w-full border rounded-lg px-3 py-2" rows={2} value={brandDraft.enrollment_message} onChange={(e) => setBrandDraft({ ...brandDraft, enrollment_message: e.target.value })} />
              </label>
              <label className="block text-sm">
                Support contact
                <input className="mt-1 w-full border rounded-lg px-3 py-2" value={brandDraft.support_contact} onChange={(e) => setBrandDraft({ ...brandDraft, support_contact: e.target.value })} />
              </label>
              <button
                type="button"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm inline-flex items-center gap-1"
                onClick={async () => {
                  try {
                    await apiService.platformUpdateBranding(selectedId, brandDraft);
                    await openDetail(selectedId);
                    toast.success('Branding saved');
                  } catch (e: any) {
                    toast.error(e?.message || 'Failed to save branding');
                  }
                }}
              >
                <Palette size={14} /> Save branding
              </button>
            </div>
          )}

          {detailTab === 'Enrollment Settings' && (
            <div className="max-w-2xl space-y-3 text-sm">
              <label className="block">
                Enabled OS (comma-separated)
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2 font-mono text-xs"
                  value={enrollDraft.enabled_os}
                  onChange={(e) => setEnrollDraft({ ...enrollDraft, enabled_os: e.target.value })}
                />
              </label>
              <label className="block">
                Allowed modes (JSON)
                <textarea
                  className="mt-1 w-full border rounded-lg px-3 py-2 font-mono text-xs"
                  rows={6}
                  value={enrollDraft.allowed_modes_json}
                  onChange={(e) => setEnrollDraft({ ...enrollDraft, allowed_modes_json: e.target.value })}
                />
              </label>
              <label className="block">
                Default policies per OS (JSON map of os → policy_id)
                <textarea
                  className="mt-1 w-full border rounded-lg px-3 py-2 font-mono text-xs"
                  rows={4}
                  value={enrollDraft.default_policies_json}
                  onChange={(e) => setEnrollDraft({ ...enrollDraft, default_policies_json: e.target.value })}
                />
              </label>
              <button
                type="button"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm"
                onClick={async () => {
                  try {
                    await apiService.platformUpdateEnrollmentSettings(selectedId!, {
                      enabled_os: enrollDraft.enabled_os.split(',').map((s) => s.trim()).filter(Boolean),
                      allowed_modes: JSON.parse(enrollDraft.allowed_modes_json || '{}'),
                      default_policies: JSON.parse(enrollDraft.default_policies_json || '{}'),
                    });
                    await openDetail(selectedId!);
                    toast.success('Enrollment settings saved');
                  } catch (e: any) {
                    toast.error(e?.message || 'Failed to save enrollment settings');
                  }
                }}
              >
                Save enrollment settings
              </button>
            </div>
          )}

          {detailTab === 'Enrollment Invites' && selectedId && (
            <BulkEnrollmentPanel tenantId={selectedId} />
          )}

          {detailTab === 'Policies & Defaults' && (
            <div className="max-w-2xl space-y-3 text-sm">
              <p className="text-slate-600">Tenant OS default policy IDs used when an invite has no policy.</p>
              <textarea
                className="w-full border rounded-lg px-3 py-2 font-mono text-xs"
                rows={8}
                value={enrollDraft.default_policies_json}
                onChange={(e) => setEnrollDraft({ ...enrollDraft, default_policies_json: e.target.value })}
              />
              <button
                type="button"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm"
                onClick={async () => {
                  try {
                    await apiService.platformUpdateEnrollmentSettings(selectedId!, {
                      default_policies: JSON.parse(enrollDraft.default_policies_json || '{}'),
                    });
                    await openDetail(selectedId!);
                    toast.success('Policy defaults saved');
                  } catch (e: any) {
                    toast.error(e?.message || 'Failed to save defaults');
                  }
                }}
              >
                Save policy defaults
              </button>
            </div>
          )}

          {detailTab === 'Security / SSO' && (
            <div className="max-w-2xl space-y-3 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={securityDraft.sso_enabled}
                  onChange={(e) => setSecurityDraft({ ...securityDraft, sso_enabled: e.target.checked })}
                />
                SSO enabled (OIDC — Login page shows “Sign in with SSO” for this tenant)
              </label>
              <p className="text-xs text-slate-500">
                Expected <code>sso_config</code> keys: <code>issuer</code>, <code>client_id</code>,{' '}
                <code>client_secret</code>, <code>authorize_url</code>, <code>token_url</code>,{' '}
                <code>redirect_uri</code>, <code>scopes</code>. Start URL:{' '}
                <code>/api/v1/auth/sso/start?tenant=CODE</code>
              </p>
              <label className="block">
                SSO config (JSON)
                <textarea
                  className="mt-1 w-full border rounded-lg px-3 py-2 font-mono text-xs"
                  rows={7}
                  value={securityDraft.sso_config_json}
                  onChange={(e) => setSecurityDraft({ ...securityDraft, sso_config_json: e.target.value })}
                  placeholder={`{\n  "provider": "oidc",\n  "issuer": "https://idp.example.com",\n  "client_id": "...",\n  "client_secret": "...",\n  "authorize_url": "https://idp.example.com/authorize",\n  "token_url": "https://idp.example.com/oauth/token",\n  "redirect_uri": "http://localhost:3000/login",\n  "scopes": "openid profile email"\n}`}
                />
              </label>
              <label className="block">
                Password policy (JSON)
                <textarea
                  className="mt-1 w-full border rounded-lg px-3 py-2 font-mono text-xs"
                  rows={4}
                  value={securityDraft.password_policy_json}
                  onChange={(e) => setSecurityDraft({ ...securityDraft, password_policy_json: e.target.value })}
                />
              </label>
              <button
                type="button"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm"
                onClick={async () => {
                  try {
                    await apiService.platformUpdateSecurity(selectedId!, {
                      sso_enabled: securityDraft.sso_enabled,
                      sso_config: JSON.parse(securityDraft.sso_config_json || '{}'),
                      password_policy: JSON.parse(securityDraft.password_policy_json || '{}'),
                    });
                    await openDetail(selectedId!);
                    toast.success('Security settings saved');
                  } catch (e: any) {
                    toast.error(e?.message || 'Failed to save security');
                  }
                }}
              >
                Save security
              </button>
            </div>
          )}

          {detailTab === 'Compliance Rules' && (
            <CompliancePackEditor
              tenantId={selectedId!}
              deviceCount={detail.device_count ?? 0}
            />
          )}

          {detailTab === 'License / Limits' && (
            <div className="max-w-md space-y-3 text-sm">
              <label className="block">
                Max devices
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={licenseDraft.max_devices}
                  onChange={(e) => setLicenseDraft({ ...licenseDraft, max_devices: e.target.value })}
                />
              </label>
              <label className="block">
                Max users
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={licenseDraft.max_users}
                  onChange={(e) => setLicenseDraft({ ...licenseDraft, max_users: e.target.value })}
                />
              </label>
              <button
                type="button"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm"
                onClick={async () => {
                  try {
                    await apiService.platformUpdateTenant(selectedId!, {
                      license_limits: {
                        max_devices: Number(licenseDraft.max_devices) || 0,
                        max_users: Number(licenseDraft.max_users) || 0,
                      },
                    });
                    await openDetail(selectedId!);
                    toast.success('License limits saved');
                  } catch (e: any) {
                    toast.error(e?.message || 'Failed to save limits');
                  }
                }}
              >
                Save limits
              </button>
            </div>
          )}

          {detailTab === 'Package Storage' && (
            <div className="max-w-lg space-y-3 text-sm">
              <p className="text-slate-600">
                Allocate a UEM-hosted folder under <span className="font-mono text-xs">/opt/uem/package-store/</span> and a disk quota.
                Client admins can upload installers into this space, or use their own share if allowed.
              </p>
              {detail?.package && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs space-y-1">
                  <p><span className="text-slate-500">Absolute path:</span> <span className="font-mono">{detail.package.uem_absolute_path || '—'}</span></p>
                  <p>
                    <span className="text-slate-500">Usage:</span>{' '}
                    {detail.package.used_gb ?? 0} / {detail.package.quota_gb ?? 0} GB
                  </p>
                  <p><span className="text-slate-500">Mode:</span> {detail.package.source_mode || 'uem_hosted'}</p>
                </div>
              )}
              <label className="block">
                Folder name (under package-store)
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2 font-mono uppercase"
                  value={packageDraft.uem_relative_path}
                  onChange={(e) =>
                    setPackageDraft({ ...packageDraft, uem_relative_path: e.target.value.toUpperCase() })
                  }
                />
              </label>
              <label className="block">
                Quota (GB)
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={packageDraft.quota_gb}
                  onChange={(e) => setPackageDraft({ ...packageDraft, quota_gb: e.target.value })}
                />
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={packageDraft.allow_client_share}
                  onChange={(e) =>
                    setPackageDraft({ ...packageDraft, allow_client_share: e.target.checked })
                  }
                />
                Allow client to use their own package share
              </label>
              <button
                type="button"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm"
                onClick={async () => {
                  try {
                    await apiService.platformUpdatePackageSettings(selectedId!, {
                      uem_relative_path: packageDraft.uem_relative_path.trim() || detail?.code,
                      quota_gb: Number(packageDraft.quota_gb) || 5,
                      allow_client_share: packageDraft.allow_client_share,
                    });
                    await openDetail(selectedId!);
                    toast.success('Package storage saved');
                  } catch (e: any) {
                    toast.error(e?.message || 'Failed to save package storage');
                  }
                }}
              >
                Save package storage
              </button>
            </div>
          )}

          {detailTab === 'Buntu' && (
            <div className="max-w-xl space-y-5 text-sm">
              <p className="text-slate-600">
                Platform controls for this client. Multi-tenant compose/history is under{' '}
                <button
                  type="button"
                  className="text-indigo-600 hover:underline"
                  onClick={() => onNavigate?.('platform-buntu')}
                >
                  Buntu Broadcasts
                </button>
                . Tenant admins still configure wellness/branding in Settings → Buntu.
              </p>
              {buntuLoading ? (
                <p className="text-slate-500">Loading Buntu settings…</p>
              ) : (
                <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-4 space-y-3">
                  <label className="flex items-start gap-2 text-slate-700">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={buntuDraft.enabled}
                      onChange={(e) =>
                        setBuntuDraft({
                          ...buntuDraft,
                          enabled: e.target.checked,
                          required_for_admins: e.target.checked ? buntuDraft.required_for_admins : false,
                        })
                      }
                    />
                    <span>
                      <span className="font-medium">Enable Buntu for this client</span>
                      <span className="block text-slate-500 text-xs">
                        Companion appears in Hub browser sessions for this tenant
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-slate-700">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={buntuDraft.required_for_admins}
                      disabled={!buntuDraft.enabled}
                      onChange={(e) =>
                        setBuntuDraft({ ...buntuDraft, required_for_admins: e.target.checked })
                      }
                    />
                    <span>
                      <span className="font-medium">Required for Client Admin Hub logins</span>
                      <span className="block text-slate-500 text-xs">
                        Tenant admins cannot minimize or hide Buntu (super-admin broadcasts still reach them)
                      </span>
                    </span>
                  </label>
                  <button
                    type="button"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm"
                    onClick={async () => {
                      try {
                        await apiService.platformUpdateTenantBuntuSettings(selectedId!, {
                          enabled: buntuDraft.enabled,
                          required_for_admins: buntuDraft.required_for_admins,
                        });
                        toast.success('Buntu settings saved');
                      } catch (e: any) {
                        toast.error(e?.message || 'Failed to save Buntu settings');
                      }
                    }}
                  >
                    Save Buntu settings
                  </button>
                </div>
              )}

              <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
                <div className="flex items-center gap-2 font-medium text-slate-800">
                  <Send size={16} className="text-indigo-600" />
                  Message this tenant
                </div>
                <textarea
                  className="w-full border rounded-lg px-3 py-2 text-sm min-h-[90px]"
                  placeholder="Message for Hub users in this organisation…"
                  maxLength={500}
                  value={buntuMsg.body}
                  onChange={(e) => setBuntuMsg({ ...buntuMsg, body: e.target.value })}
                />
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-xs text-slate-500">
                    Priority
                    <select
                      className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm text-slate-800"
                      value={buntuMsg.priority}
                      onChange={(e) => setBuntuMsg({ ...buntuMsg, priority: e.target.value })}
                    >
                      <option value="info">Info</option>
                      <option value="important">Important</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </label>
                  <label className="block text-xs text-slate-500">
                    Delivery
                    <select
                      className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm text-slate-800"
                      value={buntuMsg.delivery}
                      onChange={(e) => setBuntuMsg({ ...buntuMsg, delivery: e.target.value })}
                    >
                      <option value="once">Show once</option>
                      <option value="until_dismissed">Until dismissed</option>
                      <option value="sticky">Sticky</option>
                    </select>
                  </label>
                  <label className="block text-xs text-slate-500 col-span-2">
                    Audience
                    <select
                      className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm text-slate-800"
                      value={buntuMsg.audience}
                      onChange={(e) => setBuntuMsg({ ...buntuMsg, audience: e.target.value })}
                    >
                      <option value="client_admins">Client Admins only</option>
                      <option value="all">All Hub users</option>
                    </select>
                  </label>
                </div>
                {buntuMsg.delivery === 'sticky' && (
                  <label className="block text-xs text-slate-500">
                    Sticky minutes
                    <input
                      type="number"
                      min={1}
                      max={240}
                      className="mt-1 w-28 border rounded-lg px-2 py-1.5 text-sm"
                      value={buntuMsg.sticky_minutes}
                      onChange={(e) =>
                        setBuntuMsg({
                          ...buntuMsg,
                          sticky_minutes: Math.max(1, Math.min(240, Number(e.target.value) || 5)),
                        })
                      }
                    />
                  </label>
                )}
                <button
                  type="button"
                  disabled={buntuMsgSending || !buntuMsg.body.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm disabled:opacity-50"
                  onClick={async () => {
                    setBuntuMsgSending(true);
                    try {
                      await apiService.platformBuntuBroadcast({
                        body: buntuMsg.body.trim(),
                        priority: buntuMsg.priority,
                        delivery: buntuMsg.delivery,
                        sticky_minutes: buntuMsg.sticky_minutes,
                        audience: buntuMsg.audience,
                        tenant_ids: [selectedId!],
                      });
                      toast.success('Message sent to this tenant', 'Buntu broadcast');
                      setBuntuMsg({ ...buntuMsg, body: '' });
                    } catch (e: any) {
                      toast.error(e?.message || 'Failed to send');
                    } finally {
                      setBuntuMsgSending(false);
                    }
                  }}
                >
                  <Send size={14} />
                  {buntuMsgSending ? 'Sending…' : 'Send to this tenant'}
                </button>
              </div>
            </div>
          )}

          {detailTab === 'Audit Logs' && (
            <div className="overflow-auto max-h-96">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2">When</th>
                    <th>Action</th>
                    <th>Actor</th>
                    <th>Entity</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((a) => (
                    <tr key={a.id} className="border-b border-slate-50">
                      <td className="py-2 whitespace-nowrap text-xs">{a.created_at ? new Date(a.created_at).toLocaleString() : '—'}</td>
                      <td className="font-mono text-xs">{a.action}</td>
                      <td>{a.actor_username || '—'}</td>
                      <td className="text-xs">{a.entity_type} {a.entity_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Building2 size={22} /> Platform Admin — Clients
          </h2>
          <p className="text-sm text-slate-500 mt-1">Manage tenant companies, modules, users, and branding.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={loadTenants} className="inline-flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm hover:bg-slate-50">
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                const res = await apiService.importBuntuDevices();
                toast.success(`Imported ${res?.imported ?? 0} Buntu device(s) into Nutrinium`, 'Import complete');
                loadTenants();
              } catch (e: any) {
                toast.error(e?.message || 'Buntu import failed (is MDM API reachable?)');
              }
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm hover:bg-slate-50"
            title="Pull devices from Buntu MDM into Nutrinium registry"
          >
            Import Buntu
          </button>
          <button
            type="button"
            onClick={() => {
              if (onNavigate) {
                onNavigate('platform-buntu');
                return;
              }
              setBuntuBroadcast({ body: '', priority: 'important', delivery: 'until_dismissed' });
              setShowBuntuBroadcast(true);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm hover:bg-slate-50"
            title="Open Buntu Broadcasts in the Platform Admin center"
          >
            <MessageSquare size={14} /> Buntu broadcasts
          </button>
          <button
            type="button"
            onClick={() => {
              setCreateForm({ ...EMPTY_CREATE });
              setCodeTouched(false);
              setCreateError(null);
              setShowCreate(true);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm"
          >
            <Plus size={14} /> New client
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg px-3 py-2">{error}</div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading clients…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-4 py-3">Company Name</th>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Modules</th>
                <th className="px-4 py-3">Users</th>
                <th className="px-4 py-3">Devices</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/80">
                  <td className="px-4 py-3 font-semibold text-slate-800">{t.name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{t.code}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      t.status === 'active' || t.status === 'trial'
                        ? 'bg-emerald-50 text-emerald-700'
                        : t.status === 'deleted'
                          ? 'bg-red-50 text-red-700'
                          : 'bg-amber-50 text-amber-700'
                    }`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{(t.enabled_modules || []).length}</td>
                  <td className="px-4 py-3">{t.user_count ?? 0}</td>
                  <td className="px-4 py-3">{t.device_count ?? 0}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 space-x-3">
                    <button
                      type="button"
                      onClick={() => openDetail(t.id)}
                      className="text-indigo-600 hover:underline font-medium"
                    >
                      Configure
                    </button>
                    {onEnterPortal && (
                      <button
                        type="button"
                        onClick={() => onEnterPortal(t.id, t.name)}
                        className="text-slate-700 hover:underline font-medium"
                      >
                        Enter portal
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {tenants.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    No clients yet. Create one or run the Nutrinium seed migration.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl space-y-3 my-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold">Create client</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600">Company name</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="Company name"
                  value={createForm.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setCreateForm((prev) => ({
                      ...prev,
                      name,
                      code: codeTouched ? prev.code : suggestTenantCode(name),
                    }));
                  }}
                  autoFocus
                />
              </div>
              <div>
                <div className="flex items-center justify-between gap-2">
                  <label className="block text-xs font-medium text-slate-600">Code</label>
                  <button
                    type="button"
                    className="text-[11px] text-indigo-600 hover:underline"
                    onClick={() => {
                      setCodeTouched(false);
                      setCreateForm((prev) => ({ ...prev, code: suggestTenantCode(prev.name) }));
                    }}
                  >
                    Regenerate
                  </button>
                </div>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm font-mono uppercase"
                  value={createForm.code}
                  onChange={(e) => {
                    setCodeTouched(true);
                    setCreateForm({ ...createForm, code: e.target.value.toUpperCase() });
                  }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Primary domain</label>
                <input className="w-full border rounded-lg px-3 py-2 text-sm" value={createForm.primary_domain} onChange={(e) => setCreateForm({ ...createForm, primary_domain: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Timezone</label>
                <input className="w-full border rounded-lg px-3 py-2 text-sm" value={createForm.timezone} onChange={(e) => setCreateForm({ ...createForm, timezone: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Plan name</label>
                <input className="w-full border rounded-lg px-3 py-2 text-sm" value={createForm.plan_name} onChange={(e) => setCreateForm({ ...createForm, plan_name: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Billing cycle</label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm" value={createForm.billing_cycle} onChange={(e) => setCreateForm({ ...createForm, billing_cycle: e.target.value })}>
                  <option value="monthly">monthly</option>
                  <option value="annual">annual</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Trial days</label>
                <input type="number" className="w-full border rounded-lg px-3 py-2 text-sm" value={createForm.trial_days} onChange={(e) => setCreateForm({ ...createForm, trial_days: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Plan amount</label>
                <input type="number" className="w-full border rounded-lg px-3 py-2 text-sm" value={createForm.plan_amount} onChange={(e) => setCreateForm({ ...createForm, plan_amount: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Max devices</label>
                <input type="number" className="w-full border rounded-lg px-3 py-2 text-sm" value={createForm.max_devices} onChange={(e) => setCreateForm({ ...createForm, max_devices: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Max users</label>
                <input type="number" className="w-full border rounded-lg px-3 py-2 text-sm" value={createForm.max_users} onChange={(e) => setCreateForm({ ...createForm, max_users: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-600">Enabled OS (comma-separated)</label>
                <input className="w-full border rounded-lg px-3 py-2 text-sm font-mono" value={createForm.enabled_os} onChange={(e) => setCreateForm({ ...createForm, enabled_os: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Client admin name</label>
                <input className="w-full border rounded-lg px-3 py-2 text-sm" value={createForm.client_admin_name} onChange={(e) => setCreateForm({ ...createForm, client_admin_name: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Client admin email</label>
                <input className="w-full border rounded-lg px-3 py-2 text-sm" value={createForm.client_admin_email} onChange={(e) => setCreateForm({ ...createForm, client_admin_email: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Client admin phone</label>
                <input className="w-full border rounded-lg px-3 py-2 text-sm" value={createForm.client_admin_phone} onChange={(e) => setCreateForm({ ...createForm, client_admin_phone: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Client admin password</label>
                <input type="password" className="w-full border rounded-lg px-3 py-2 text-sm" value={createForm.client_admin_password} onChange={(e) => setCreateForm({ ...createForm, client_admin_password: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Support email</label>
                <input className="w-full border rounded-lg px-3 py-2 text-sm" value={createForm.support_email} onChange={(e) => setCreateForm({ ...createForm, support_email: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Billing email</label>
                <input className="w-full border rounded-lg px-3 py-2 text-sm" value={createForm.billing_email} onChange={(e) => setCreateForm({ ...createForm, billing_email: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Package folder</label>
                <input className="w-full border rounded-lg px-3 py-2 text-sm font-mono uppercase" value={createForm.package_path} onChange={(e) => setCreateForm({ ...createForm, package_path: e.target.value.toUpperCase() })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Package quota (GB)</label>
                <input type="number" className="w-full border rounded-lg px-3 py-2 text-sm" value={createForm.package_quota_gb} onChange={(e) => setCreateForm({ ...createForm, package_quota_gb: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Theme color</label>
                <input type="color" className="w-full border rounded-lg px-1 py-1 h-10" value={createForm.theme_color} onChange={(e) => setCreateForm({ ...createForm, theme_color: e.target.value })} />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={createForm.allow_client_share} onChange={(e) => setCreateForm({ ...createForm, allow_client_share: e.target.checked })} />
                  Allow client package share
                </label>
              </div>
              <div className="md:col-span-2 rounded-lg border border-indigo-100 bg-indigo-50/60 p-3 space-y-2">
                <div className="text-xs font-semibold text-indigo-800 uppercase tracking-wide">Buntu companion (Hub browser)</div>
                <label className="flex items-start gap-2 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={createForm.buntu_enabled}
                    onChange={(e) => setCreateForm({ ...createForm, buntu_enabled: e.target.checked })}
                  />
                  <span>
                    <span className="font-medium">Enable Buntu for this client</span>
                    <span className="block text-slate-500">Shows the companion in Hub browser sessions for this tenant</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={createForm.buntu_required_for_admins}
                    disabled={!createForm.buntu_enabled}
                    onChange={(e) => setCreateForm({ ...createForm, buntu_required_for_admins: e.target.checked })}
                  />
                  <span>
                    <span className="font-medium">Required for Client Admin Hub logins</span>
                    <span className="block text-slate-500">
                      Tenant admins cannot minimize or hide Buntu (super-admin broadcasts still reach them)
                    </span>
                  </span>
                </label>
              </div>
            </div>
            {createError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {createError}
              </div>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                className="px-3 py-2 text-sm border rounded-lg"
                onClick={() => {
                  setShowCreate(false);
                  setCreateError(null);
                }}
              >
                Cancel
              </button>
              <button type="button" disabled={saving || !createForm.name.trim()} className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg disabled:opacity-50" onClick={handleCreate}>
                {saving ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBuntuBroadcast && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <MessageSquare size={18} className="text-indigo-600" />
                  Message all tenant admins
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Sends via Buntu to every active Client Admin Hub login across all tenants. Browser companion only
                  (desktop agents later).
                </p>
              </div>
              <button type="button" className="text-slate-400 hover:text-slate-600" onClick={() => setShowBuntuBroadcast(false)}>
                <X size={18} />
              </button>
            </div>
            <textarea
              className="w-full border rounded-lg px-3 py-2 text-sm min-h-[120px]"
              placeholder="e.g. Platform maintenance tonight 22:00 UTC — warn your users."
              value={buntuBroadcast.body}
              maxLength={500}
              onChange={(e) => setBuntuBroadcast({ ...buntuBroadcast, body: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600">Priority</label>
                <select
                  className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm"
                  value={buntuBroadcast.priority}
                  onChange={(e) => setBuntuBroadcast({ ...buntuBroadcast, priority: e.target.value })}
                >
                  <option value="info">Info</option>
                  <option value="important">Important</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">Delivery</label>
                <select
                  className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm"
                  value={buntuBroadcast.delivery}
                  onChange={(e) => setBuntuBroadcast({ ...buntuBroadcast, delivery: e.target.value })}
                >
                  <option value="once">Show once</option>
                  <option value="until_dismissed">Until dismissed</option>
                  <option value="sticky">Sticky (5 min)</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" className="px-3 py-2 text-sm border rounded-lg" onClick={() => setShowBuntuBroadcast(false)}>
                Cancel
              </button>
              <button
                type="button"
                disabled={buntuSending || !buntuBroadcast.body.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg disabled:opacity-50"
                onClick={async () => {
                  setBuntuSending(true);
                  try {
                    const res = await apiService.platformBuntuBroadcastAdmins({
                      body: buntuBroadcast.body.trim(),
                      priority: buntuBroadcast.priority,
                      delivery: buntuBroadcast.delivery,
                      sticky_minutes: 5,
                    });
                    const n = res?.data?.total_sent ?? res?.total_sent ?? 0;
                    toast.success(`Sent to ${n} tenant(s)`, 'Buntu broadcast');
                    setShowBuntuBroadcast(false);
                  } catch (e: any) {
                    toast.error(e?.message || 'Broadcast failed');
                  } finally {
                    setBuntuSending(false);
                  }
                }}
              >
                <Send size={14} />
                {buntuSending ? 'Sending…' : 'Send to all tenant admins'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
